import { useQueryClient } from '@tanstack/react-query';
import { createContext, type FormEvent, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { CodeInput } from '../features/auth/CodeInput';
import { Button, Field, Input, Sheet } from '../ui';
import { ApiError, errorMessage, http } from './api';
import { keys } from './query';

type Runner = <T>(fn: () => Promise<T>) => Promise<T>;

const ReauthContext = createContext<Runner | null>(null);

/**
 * Esegue un'azione protetta; se il server chiede la ri-conferma (codice o password),
 * mostra il foglio, poi riprova da solo.
 */
export function ReauthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef<{ resolve: () => void; reject: (e: unknown) => void } | null>(null);

  const ask = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        pending.current = { resolve, reject };
        setCode('');
        setPassword('');
        setError(null);
        setOpen(true);
      }),
    [],
  );

  const run = useCallback<Runner>(async (fn) => {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'reauth_required') {
        await ask();
        return fn();
      }
      throw err;
    }
  }, [ask]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await http.post('/api/auth/reauth', usePassword ? { password } : { code });
      qc.invalidateQueries({ queryKey: keys.auth });
      setOpen(false);
      pending.current?.resolve();
      pending.current = null;
    } catch (err) {
      setCode('');
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ReauthContext.Provider value={run}>
      {children}
      <Sheet
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            pending.current?.reject(new ApiError(403, 'reauth_cancelled', 'Operazione annullata'));
            pending.current = null;
          }
        }}
        title="Conferma che sei tu"
        description={usePassword ? 'Scrivi la password di Regia.' : 'Scrivi il codice dell’app authenticator. Vale 5 minuti.'}
        size="sm"
        footer={
          <Button type="submit" form="reauth-form" block loading={busy} disabled={usePassword ? !password : code.length !== 6}>
            Conferma
          </Button>
        }
      >
        <form id="reauth-form" onSubmit={submit} className="flex flex-col gap-3 pt-2" noValidate>
          {usePassword ? (
            <Field label="Password" error={error ?? undefined}>
              {(id, d) => (
                <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-describedby={d} autoFocus />
              )}
            </Field>
          ) : (
            <Field label="Codice a 6 cifre" error={error ?? undefined}>
              {(id, d) => <CodeInput id={id} value={code} onChange={setCode} describedBy={d} invalid={Boolean(error)} autoFocus />}
            </Field>
          )}
          <button type="button" onClick={() => setUsePassword((v) => !v)} className="self-start text-sm font-semibold text-action hover:underline">
            {usePassword ? 'Usa il codice' : 'Usa la password'}
          </button>
        </form>
      </Sheet>
    </ReauthContext.Provider>
  );
}

export function useReauth(): Runner {
  const run = useContext(ReauthContext);
  if (!run) throw new Error('useReauth fuori da ReauthProvider');
  return run;
}
